import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useState } from "react";

/**
 * Server-paged list hook. Every list endpoint in tilt-admin-api now
 * returns {items, total, limit, offset, has_more}; this hook
 * centralises the state + TanStack Query wiring so individual pages
 * don't have to re-implement page math, search debouncing, and the
 * "keepPreviousData on page flip" UX pattern.
 *
 * Callers supply a queryKey prefix (e.g. "locations") and a fetcher
 * that takes {limit, offset, q, …extras} and returns a body shape
 * that matches the envelope. The returned shape exposes:
 *
 *   - items        rows for the current page
 *   - total        total matching rows across all pages
 *   - limit/offset current page window
 *   - pageCount    derived from total / limit
 *   - pageIndex    current page (0-based)
 *   - setPageIndex jump to a specific page
 *   - setSearch    sets q + resets offset to 0
 *   - search       current search string (controlled)
 *   - hasPrev/hasNext  whether the nav buttons should be enabled
 *   - query        the raw UseQueryResult for loading/error states
 *
 * Design notes:
 *   - We debounce the search string inside the component to avoid
 *     one refetch per keystroke. 250ms matches the admin-portal's
 *     existing "feels instant" threshold.
 *   - keepPreviousData on page flip is crucial - without it the
 *     whole table would flash to a skeleton on every Next click.
 *   - We never return a stale-but-previous `total` from inside the
 *     hook; pageCount recomputes on every render so the footer
 *     stays accurate.
 */

export interface PageEnvelope<TItem> {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  items: TItem[];
}

export interface PaginatedListOptions<TItem, TExtras> {
  queryKey: unknown[];
  fetcher: (args: {
    limit: number;
    offset: number;
    q: string | undefined;
    extras: TExtras;
  }) => Promise<PageEnvelope<TItem>>;
  extras?: TExtras;
  initialLimit?: number;
  initialPageIndex?: number;
  // How long the search input waits after last keystroke before
  // firing a new request. 0 disables debouncing (useful in tests).
  searchDebounceMs?: number;
  // Forward to TanStack Query - surface for callers that want to
  // gate the query on something external.
  enabled?: boolean;
}

export interface PaginatedListState<TItem> {
  items: TItem[];
  total: number;
  limit: number;
  offset: number;
  pageIndex: number;
  pageCount: number;
  hasPrev: boolean;
  hasNext: boolean;
  search: string;
  setSearch: (next: string) => void;
  setPageIndex: (next: number) => void;
  query: UseQueryResult<PageEnvelope<TItem>>;
}

const DEFAULT_LIMIT = 50;
const DEBOUNCE_MS = 250;

/**
 * Lightweight debounce hook - avoids pulling in lodash for one use.
 * Returns a stable value that trails `value` by `delay` ms of idle.
 * When delay ≤ 0 the value updates synchronously (useful in tests).
 */
function useDebounced<T>(value: T, delay: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    if (delay <= 0) {
      setV(value);
      return;
    }
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export function usePaginatedList<TItem, TExtras = undefined>(
  opts: PaginatedListOptions<TItem, TExtras>,
): PaginatedListState<TItem> {
  const [pageIndex, setPageIndex] = useState(opts.initialPageIndex ?? 0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, opts.searchDebounceMs ?? DEBOUNCE_MS);
  const limit = opts.initialLimit ?? DEFAULT_LIMIT;

  const offset = pageIndex * limit;
  const q = debouncedSearch.trim() === "" ? undefined : debouncedSearch.trim();

  const query = useQuery<PageEnvelope<TItem>>({
    queryKey: [...opts.queryKey, { limit, offset, q, extras: opts.extras ?? null }],
    queryFn: () =>
      opts.fetcher({
        limit,
        offset,
        q,
        extras: (opts.extras ?? (undefined as unknown as TExtras)) as TExtras,
      }),
    placeholderData: keepPreviousData,
    enabled: opts.enabled ?? true,
  });

  const total = query.data?.total ?? 0;
  const items = query.data?.items ?? [];
  const pageCount = Math.max(1, Math.ceil(total / limit));

  const boundedSetSearch = (next: string): void => {
    setSearch(next);
    // Searching should reset to page 0 - otherwise a user on page 5
    // searches for "foo" and gets an empty "page 5 of 1" state.
    setPageIndex(0);
  };

  return {
    items,
    total,
    limit,
    offset,
    pageIndex,
    pageCount,
    hasPrev: pageIndex > 0,
    hasNext: pageIndex + 1 < pageCount,
    search,
    setSearch: boundedSetSearch,
    setPageIndex,
    query,
  };
}
